import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { useState, useEffect } from 'react';

import { PostResponse } from '@/api/endpoints/community/posts';
import { Send01Icon } from '@/assets/icons/Send01Icon';

export interface PostFormData {
  title: string;
  content: string;
  is_pinned?: boolean;
  metadata?: Record<string, unknown>;
}

interface PostFormProps {
  onSubmit: (data: PostFormData) => Promise<void>;
  onCancel?: () => void;
  initialData?: Partial<PostResponse>;
  isLoading?: boolean;
  error?: string;
  mode?: 'create' | 'edit';
  showPinOption?: boolean;
  title?: string;
}

export const PostForm: React.FC<PostFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isLoading = false,
  error,
  mode = 'create',
  showPinOption = false,
  title,
}) => {
  const [formData, setFormData] = useState<PostFormData>({
    title: initialData?.title || '',
    content: initialData?.content || '',
    is_pinned: initialData?.is_pinned || false,
  });

  const [formErrors, setFormErrors] = useState<Partial<Record<keyof PostFormData, string>>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        content: initialData.content || '',
        is_pinned: initialData.is_pinned || false,
      });
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof PostFormData, string>> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.trim().length > 200) {
      errors.title = 'Title must be 200 characters or less';
    }

    if (!formData.content.trim()) {
      errors.content = 'Content is required';
    } else if (formData.content.trim().length > 10000) {
      errors.content = 'Content must be 10,000 characters or less';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        ...formData,
        title: formData.title.trim(),
        content: formData.content.trim(),
      });
    } catch (err) {
      // Error handling is done by parent component
    }
  };

  const handleInputChange =
    (field: keyof PostFormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value =
        e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear error for this field when user starts typing
        if (formErrors[field]) {
          setFormErrors(prev => ({ ...prev, [field]: undefined }));
        }
      };

  const handleSwitchChange =
    (field: keyof PostFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({ ...prev, [field]: e.target.checked }));
    };

  const isFormValid = formData.title.trim() && formData.content.trim();

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'secondary.900',
            mb: 3,
          }}
        >
          {title || (mode === 'create' ? 'Create New Post' : 'Edit Post')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Title Field */}
            <TextField
              label="Post Title"
              value={formData.title}
              onChange={handleInputChange('title')}
              error={!!formErrors.title}
              helperText={formErrors.title || `${formData.title.length}/200 characters`}
              fullWidth
              required
              disabled={isLoading}
              inputProps={{
                maxLength: 200,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            {/* Content Field */}
            <TextField
              label="Post Content"
              value={formData.content}
              onChange={handleInputChange('content')}
              error={!!formErrors.content}
              helperText={formErrors.content || `${formData.content.length}/10,000 characters`}
              fullWidth
              required
              multiline
              rows={6}
              disabled={isLoading}
              inputProps={{
                maxLength: 10000,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            {/* Pin Option */}
            {showPinOption && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_pinned}
                    onChange={handleSwitchChange('is_pinned')}
                    disabled={isLoading}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Pin this post
                    </Typography>
                    <Typography variant="caption" color="secondary.600">
                      Pinned posts appear at the top of the list
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 0.5 }}
              />
            )}

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  isLoading ? <CircularProgress size={16} color="inherit" /> : <Send01Icon />
                }
                disabled={!isFormValid || isLoading}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                  },
                  '&:disabled': {
                    bgcolor: 'secondary.300',
                    color: 'secondary.500',
                    boxShadow: 'none',
                  },
                }}
              >
                {(() => {
                  if (isLoading) {
                    return mode === 'create' ? 'Creating...' : 'Updating...';
                  }
                  return mode === 'create' ? 'Create Post' : 'Update Post';
                })()}
              </Button>

              {onCancel && (
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  disabled={isLoading}
                  sx={{
                    borderColor: 'secondary.300',
                    color: 'secondary.700',
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'secondary.400',
                      bgcolor: 'secondary.50',
                    },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};
